import { usePostHog } from 'posthog-js/react';
import { useEffect, useState } from 'react';
import { PiCookieFill } from 'react-icons/pi';
import { styled } from 'styled-components';
import { FuuzeColors } from '../styles/colors.js';
import { mediaQuery, sizes } from '../styles/styles.js';
import { FuuzeButton } from './fuuze-button.js';
import { FlexColumn, FlexRow } from './fuuze-flex.js';
import { FuuzeText } from './fuuze-text.js';

type CookieBannerProps = {
  title: string;
  declineText: string;
  acceptText: string;
};

export function CookieBanner({ title, declineText, acceptText }: CookieBannerProps) {
  const [consentGiven, setConsentGiven] = useState('');
  const posthog = usePostHog();

  useEffect(() => {
    setConsentGiven(cookieConsentGiven());
  }, []);

  useEffect(() => {
    if (consentGiven !== '') {
      posthog?.set_config({ persistence: consentGiven === 'yes' ? 'localStorage+cookie' : 'memory' });
    }
  }, [consentGiven]);

  const handleAcceptCookies = () => {
    localStorage.setItem('cookie_consent', 'yes');
    setConsentGiven('yes');
  };

  const handleDeclineCookies = () => {
    localStorage.setItem('cookie_consent', 'no');
    setConsentGiven('no');
  };

  if (consentGiven === 'undecided') {
    return (
      <Wrapper>
        <ContentRow>
          <PiCookieFill size={24} />
          <FuuzeText>{title}</FuuzeText>
        </ContentRow>

        <ActionsRow>
          <FuuzeButton flat style={{ color: FuuzeColors.text }} type='button' onClick={handleDeclineCookies}>
            {declineText}
          </FuuzeButton>
          <FuuzeButton
            style={{ backgroundColor: FuuzeColors.orange.orange100, borderRadius: 100 }}
            type='button'
            onClick={handleAcceptCookies}
          >
            {acceptText}
          </FuuzeButton>
        </ActionsRow>
      </Wrapper>
    );
  }

  return null;
}

const Wrapper = styled(FlexColumn)`
  gap: 32px;
  background: ${props => props.theme.colors.dark.dark200};
  border-radius: 8px;
  left: 50%;
  padding: 16px;
  position: fixed;
  transform: translate(-50%);
  width: 90%;
  z-index: 100;
  animation: smooth-appear 0.5s forwards cubic-bezier(0.16, 1, 0.3, 1);
  animation-delay: 1s;
  bottom: -100%;
  max-width: ${sizes.xl};

  @media ${mediaQuery.desktop} {
    flex-direction: row;
  }

  @keyframes smooth-appear {
    to {
      bottom: 64px;
    }
  }
`;

const ContentRow = styled(FlexRow)`
  flex: 1;
  align-items: center;

  svg {
    @media ${mediaQuery.tablet} {
      width: 28px;
      height: 28px;
    }
  }

  p {
    font-size: ${props => props.theme.typography.body2.fontSize}px;

    @media ${mediaQuery.tablet} {
      font-size: ${props => props.theme.typography.body.fontSize}px;
    }
  }
`;

const ActionsRow = styled(FlexRow)`
  align-self: end;
`;

export function cookieConsentGiven(): 'yes' | 'no' | 'undecided' {
  if (!localStorage.getItem('cookie_consent')) {
    return 'undecided';
  }
  return localStorage.getItem('cookie_consent') as 'yes' | 'no';
}
